import { BaseCubiqLogger } from "shared/broker-client-services/worker/BaseCubiqLogger";
import { MESSAGE_LOG_PREFIX } from "shared/broker-client-services/worker/CubiqLogger.constants";
import { CubiqLoggerConfig } from "shared/broker-client-services/worker/CubiqLoggerConfig";
import {
  AdminModelUpdateImpl,
  AdminImportStatusEventImpl,
  ApplicationMessageEventImpl,
  AutoHedgingStatus,
  AutoHedgingStatusEventImpl,
  BenchmarkLockLifeCycleEventImpl,
  BenchmarkPositionAuditImpl,
  BulkOrderLifeCycleEventImpl,
  ChangeHedgingModeEventImpl,
  ConcreteEncodable,
  DealImpl,
  DIReason,
  Encodable,
  FixingEventImpl,
  FixingOrderUpdateEventImpl,
  FixingState,
  FxLifeCycleEventType,
  GammaOrderLifeCycleEventImpl,
  HedgingModeType,
  InflightCheckEventImpl,
  InterventionEventImpl,
  InterventionOperationType,
  LockLifeCycleEventImpl,
  LockType,
  ManualNettingBulkEventImpl,
  ManualNettingEventImpl,
  MarketDataEventImpl,
  ModelUpdateImpl,
  ModelUpdateType,
  AdminImportType,
  OpsExceptionType,
  OpsExcRecord,
  OpsIntEventImpl,
  OrderData,
  OrderDealState,
  OrderLifeCycleEventImpl,
  OrderState,
  PauseLifeCycleEventImpl,
  PauseState,
  RejectSubType,
  SavedOrderEventImpl,
  SavedOrderEventListImpl,
  SysCtrlEventImpl,
  SystemControlStatus,
  SystemControlSubType,
  SystemControlType,
  SystemMessageImpl,
  SystemMonitorEventImpl,
  TradingCalendarSnapShotImpl,
  TrailingStopEventImpl,
  TransferStatus,
  UnsolicitedCancelLifeCycleEventImpl,
  XferEventImpl,
  XferType,
} from "shared/cubiq-model-multi-asset";
import {
  SYSMON_EVENT_FQDN,
  SYSMON_EVENT_GROUP_FQDN_DISP_TXT,
  SYSMON_EVENT_SOURCE,
  SYSMON_EVENT_STATUS_DISP_TXT,
} from "shared/cubiq-services/lookup/model/impl/SystemMonitorLookup.constants";

/**
 * Cubiq logger for asynchronous messages - messages of type SreviceResponseEventIpl are handled
 * by the CubiqResponseLogger class
 *
 * By default shares a common config with other loggers - message logging can be enabled/disabled by type
 * based on the message encoding type
 */
export class CubiqMessageLogger extends BaseCubiqLogger {
  constructor(config: CubiqLoggerConfig) {
    super(MESSAGE_LOG_PREFIX, config);
  }

  public processMessage(messageType: string, payload: Encodable) {
    if (!this.config.outputMessageType(messageType)) {
      // We don't to log this request
      return;
    }

    this.updateMessageId(payload);

    switch (messageType) {
      case "AutoHedgingStatusEventImpl":
        this.logAutoHedgingStatusEvent(payload as AutoHedgingStatusEventImpl);
        break;
      case "BenchmarkLockLifeCycleEventImpl":
        this.logBenchmarkLockLifeCycleEvent(payload as BenchmarkLockLifeCycleEventImpl);
        break;
      case "BenchmarkPositionAuditImpl":
        this.logBenchmarkPositionAuditEvent(payload as BenchmarkPositionAuditImpl);
        break;
      case "BulkOrderLifeCycleEventImpl":
        this.logBulkOrderLifeCycleEvent(payload as BulkOrderLifeCycleEventImpl);
        break;
      case "ChangeHedgingModeEventImpl":
        this.logChangeHedgingModeEvent(payload as ChangeHedgingModeEventImpl);
        break;
      case "DealImpl":
        this.logDealEvent(payload as DealImpl);
        break;
      case "FixingEventImpl":
        this.logFixingEvent(payload as FixingEventImpl);
        break;
      case "FixingOrderUpdateEventImpl":
        this.logFixingOrderUpdateEvent(payload as FixingOrderUpdateEventImpl);
        break;
      case "InterventionEventImpl":
        this.logInterventionEvent(payload as InterventionEventImpl);
        break;
      case "LockLifeCycleEventImpl":
        this.logLockEvent(payload as LockLifeCycleEventImpl);
        break;
      case "OrderLifeCycleEventImpl":
        this.logOrderLifeCycleEvent(payload as OrderLifeCycleEventImpl);
        break;
      case "PauseLifeCycleEventImpl":
        this.logPauseEvent(payload as PauseLifeCycleEventImpl);
        break;
      case "XferEventImpl":
        this.logXferEvent(payload as XferEventImpl);
        break;
      case "ModelUpdateImpl":
        this.logModelUpdateEvent(payload as ModelUpdateImpl);
        break;
      case "OpsIntEventImpl":
        this.logOpsInterventionEvent(payload as OpsIntEventImpl);
        break;
      case "SavedOrderEventImpl":
        this.logSavedOrderEvent(payload as SavedOrderEventImpl);
        break;
      case "SavedOrderEventListImpl":
        this.logSavedOrdersEvent(payload as SavedOrderEventListImpl);
        break;
      case "ServiceResponseImpl":
        this.alert("Should not have gotten a service response");
        break;
      case "SysCtrlEventImpl":
        this.logSysCtrlEvent(payload as SysCtrlEventImpl);
        break;
      case "SystemMonitorEventImpl":
        this.logSystemMonitorEvent(payload as SystemMonitorEventImpl);
        break;
      case "TradingCalendarSnapShotImpl":
        this.logTradingCalendarSnapShotEvent(payload as TradingCalendarSnapShotImpl);
        break;
      case "TrailingStopEventImpl":
        this.logTrailingStopEvent(payload as TrailingStopEventImpl);
        break;
      case "UnsolicitedCancelLifeCycleEventImpl":
        this.logUnsolicitedCancelLifeCycleEvent(payload as UnsolicitedCancelLifeCycleEventImpl);
        break;
      case "ApplicationMessageEventImpl":
        this.logApplicationMessageEvent(payload as ApplicationMessageEventImpl);
        break;
      case "SystemMessageImpl":
        this.logSystemMessageEvent(payload as SystemMessageImpl);
        break;
      case "AdminModelUpdateImpl":
        this.logAdminModelUpdateEvent(payload as AdminModelUpdateImpl);
        break;
      case "AdminImportStatusEventImpl":
        this.logAdminImportStatusEvent(payload as AdminImportStatusEventImpl);
      case "MarketDataEventImpl":
        this.logMarketDataEvent(payload as MarketDataEventImpl);
        break;
      case "ManualNettingEventImpl":
        this.logManualNettingEvent(payload as ManualNettingEventImpl);
        break;
      case "ManualNettingBulkEventImpl":
        this.logManualNettingBulkEvent(payload as ManualNettingBulkEventImpl);
        break;
      case "GammaOrderLifeCycleEventImpl":
        this.logGammaOrderLifeCycleEvent(payload as GammaOrderLifeCycleEventImpl);
        break;
      default:
        this.logUnsupported("Message", messageType);
        break;
    }
  }

  private logLockEvent(event: LockLifeCycleEventImpl) {
    const lockType: string =
      typeof event.lockType === "number" ? LockType[event.lockType] : "<Unlock>";
    const lockIds = event.orderIds?.join(", ") ?? "None";

    this.log(`LockEvent (${lockType}) - orders = [${lockIds}]`);
  }

  /**
   * Fixing events
   * @param event
   */
  private logFixingEvent(event: FixingEventImpl) {
    const fixingState = FixingState[event.fixingState];

    const fixingTimes =
      event.bMTimeCodes?.map((timeCode) => this.getFixingCode(timeCode)).join(", ") ??
      "<No Fixing Times>";
    this.log(`FixingEvent (${fixingState}) - fixingTimes = [${fixingTimes}]`);
  }

  /**
   * Bulk Lifecycle event
   * @param event
   */
  private logBulkOrderLifeCycleEvent(event: BulkOrderLifeCycleEventImpl) {
    const eventType = FxLifeCycleEventType[event.eventTypeId];
    const parts: string[] = [];

    this.addRejectTypePart(event.rejectType, parts);

    this.addErrorParts(event.errors, parts);
    const partsText = parts.join(", ");

    // Log out the main event then the sub events, then the complete
    this.log(`BulkOrderLifeCycle (${eventType}) -${partsText}`);
    for (const subEvent of event.events) {
      this.logOrderLifeCycleEvent(subEvent as OrderLifeCycleEventImpl);
    }
    this.log(`BulkOrderLifeCycle complete`);
  }
  /**
   * Lifecycle event
   * @param event
   */
  private logOrderLifeCycleEvent(event: OrderLifeCycleEventImpl) {
    const eventType = FxLifeCycleEventType[event.fxOrderLifeCycleEvent];
    const parts: string[] = [];

    if (!this.addOrderDataPart(event.orderData, parts)) {
      parts.push(`Order ${event.orderId}`);
      parts.push(`<empty order data>`);
    }

    this.addRejectTypePart(event.rejectType, parts);
    this.addErrorParts(event.errors, parts);

    const partsText = parts.join(", ");

    this.log(`OrderLifeCycle (${eventType}) -${partsText}`);
  }

  /**
   * Gamma order lifecycle event
   * @param event
   */
  private logGammaOrderLifeCycleEvent(event: GammaOrderLifeCycleEventImpl) {
    const eventType = FxLifeCycleEventType[event.eventTypeId];
    const parts: string[] = [];
    const events: OrderLifeCycleEventImpl[] = event.eventList ?? [];
    this.log(
      `GammaOrderLifeCycle (${eventType}) for clOrdId '${event.externalClientReference}' - ${event.eventList.length} nested events`,
    );
    for (const event of events) {
      this.logOrderLifeCycleEvent(event);
    }
    this.log(`GammaOrderLifeCycle - end`);
  }

  private logPauseEvent(event: PauseLifeCycleEventImpl) {
    const eventType = FxLifeCycleEventType[event.eventTypeId];
    const parts: string[] = [];
    if (event.unsolicited) {
      parts.push("Unsolicited");
    }
    if (event.orders && event.orders.length > 0) {
      const orders = [];
      for (const pauseData of event.orders) {
        const pauseState = PauseState[pauseData.pauseState];
        if (pauseData.isError) {
          const pauseErrors = this.getErrorMessages(pauseData.errors);
          if (pauseErrors.length) {
            orders.push(
              `Order ID ${pauseData.orderId} ${pauseState} - ERROR - [${pauseErrors.join(", ")}]`,
            );
          }
        } else {
          orders.push(`Order ID ${pauseData.orderId} ${pauseState}`);
        }
      }
      parts.push(`[${orders.join(", ")}]`);
    } else {
      parts.push("No Order Data");
    }

    const partsText = parts.join(", ");

    this.log(`PauseLifeCycle (${eventType}) -${partsText}`);
  }

  private logXferEvent(event: XferEventImpl) {
    const eventType = XferType[event.eventType];
    const parts: string[] = [];

    const targetGroup = `Target[${event.targetGroupId?.toString() ?? "None"}]`;
    parts.push(targetGroup);
    const user = `by User[${event.actioningUserId?.toString() ?? "None"}]`;
    parts.push(user);
    parts.push(
      `OrderData Count=${event.orderData?.length > 0 ? event.orderData.length : "<None>"}`,
    );
    parts.push(`Order Ids=[${event.orderIds?.length > 0 ? event.orderIds.join(",") : "<None>"}]`);
    if (event.orderData?.length > 0 && (event.orderIds == null || event.orderIds.length == 0)) {
      const orderIds = event.orderData.map((orderData) => orderData.orderId?.toString()).join(",");
      parts.push(`Model order Ids=[${orderIds}]`);
    }
    const partsText = parts.join(", ");

    this.log(`XferEvent (${eventType}) -${partsText}`);
  }
  private logModelUpdateEvent(event: ModelUpdateImpl) {
    const eventType = ModelUpdateType[event.eventType];
    const parts: string[] = [];
    if (event.entityType) {
      const entityType = this.getLastClassPart(event.entityType);
      parts.push(`Type: ${entityType}`);
    }
    if (event.payLoad) {
      const entity = event.payLoad;
      if ("id" in entity) {
        parts.push(`ID[${entity.id}]`);
      }
      if ("name" in entity) {
        parts.push(`ID[${entity.name}]`);
      }
    }
    const partsText = parts.join(", ");

    this.log(`Model Update  (${eventType}) -${partsText}`);
  }

  private logBenchmarkLockLifeCycleEvent(event: BenchmarkLockLifeCycleEventImpl) {
    const eventType = FxLifeCycleEventType[event.eventTypeId];
    const parts: string[] = [];

    // For single order, keep format as close to standard LC event as possible
    if (event.orderDatas?.length == 1) {
      this.addOrderDataPart(event.orderDatas[0], parts);
    } else {
      parts.push(
        `OrderData Count=${event.orderDatas?.length > 0 ? event.orderDatas.length : "<None>"}`,
      );
      parts.push(`Order Ids=[${event.orderIds?.length > 0 ? event.orderIds.join(",") : "<None>"}]`);
      if (event.orderDatas?.length > 0 && (event.orderIds == null || event.orderIds.length == 0)) {
        const orderIds = event.orderDatas
          .map((orderData) => orderData.orderId?.toString())
          .join(",");
        parts.push(`Model order Ids=[${orderIds}]`);
      }
    }
    if (typeof event.lockType === "number") {
      const lockType = `Lock Type = ${LockType[event.lockType]}`;
      parts.push(lockType);
    }
    const user = `by User[${event.lockUser}]`;
    parts.push(user);
    this.addErrorParts(event.errors, parts);
    const partsText = parts.join(", ");

    this.log(
      `LockBenchmarkLifeCycleEvent (${eventType} ${event.abort ? "-Abort" : ""}) -${partsText}`,
    );
  }

  private logBenchmarkPositionAuditEvent(event: BenchmarkPositionAuditImpl) {
    const parts: string[] = [];

    this.addFixingCode(event.bmTimeCode, parts);
    let orderState = "";
    if (typeof event.orderState === "number") {
      orderState = ` -  ${OrderState[event.orderState]}`;
    }
    parts.push(`Action(${event.orderAction}${orderState})`);

    parts.push(`User:${event.userCd}`);
    const partsText = parts.join(", ");

    this.log(`BenchmarkPositionAuditEvent -${partsText}`);
  }

  private logFixingOrderUpdateEvent(event: FixingOrderUpdateEventImpl) {
    const parts: string[] = [];

    let orderStateStr = "";
    if (typeof event.orderState === "number") {
      const orderState = OrderState[event.orderState];
      orderStateStr = `(${orderState})`;
    }
    parts.push(`Order ${event.orderId}${orderStateStr}`);

    const partsText = parts.join(", ");

    this.log(`FixingOrderUpdateEvent -${partsText}`);
  }

  private logAutoHedgingStatusEvent(event: AutoHedgingStatusEventImpl) {
    const parts: string[] = [];

    const status: AutoHedgingStatus = event.status;

    parts.push(`Status[${status.autoHedgingEnabled ? "Enabled" : "Disabled"}]`);
    parts.push(`User[${status.userId}]`);
    if (status.comment) {
      parts.push(`"${status.comment}"`);
    }
    const partsText = parts.join(", ");
    this.log(`AutoHedgingStatusEvent - ${partsText}`);
  }

  private logChangeHedgingModeEvent(event: ChangeHedgingModeEventImpl) {
    const parts: string[] = [];

    this.addFixingCode(event.fixing, parts);

    if (event.pairs?.length > 0) {
      parts.push(`for pairs[${event.pairs.join(",")}]`);
    }

    if (event.userId) {
      parts.push(`User[${event.userId}]`);
    }
    const partsText = parts.join(", ");
    this.log(`ChangeHedgingModeEvent (${HedgingModeType[event.hedgingModeType]}) - ${partsText}`);
  }

  private logInterventionEvent(event: InterventionEventImpl) {
    const eventType = FxLifeCycleEventType[event.eventType];
    const operationType = InterventionOperationType[event.operationType];
    const parts: string[] = [];

    if (typeof event.reason === "number") {
      const reason = `reason[${DIReason[event.reason]}]`;
      parts.push(reason);
    }

    this.addOrderDataPart(event.orderData, parts);

    if (event.lockUserId) {
      const user = `locked by User[${event.lockUserId}]`;
      parts.push(user);
    }
    if (event.unLockUserId) {
      const user = `unlocked by User[${event.unLockUserId}]`;
      parts.push(user);
    }

    if (typeof event.transferStatus === "number") {
      const data = event.transferStatus;
      parts.push(`Transfer to group ${event.targetTraderGroupId} status[${TransferStatus[data]}]`);
    }

    if (event.fixingRateRequirement) {
      parts.push(`Fixing Rate required`);
    }
    if (event.fwdRollOrderData) {
      const data = event.fwdRollOrderData;
      parts.push(
        `FwdRoll(order count[${data.orders?.length ?? 0}], portfolioId=${data.portfolioId})`,
      );
    }
    if (event.displayChannelName) {
      parts.push(`channel[${event.displayChannelName}]}`);
    }
    this.addErrorParts(event.errors, parts);

    const partsText = parts.join(", ");

    this.log(`InterventionEvent (${eventType} - ${operationType}) -${partsText}`);
  }

  private logOpsInterventionEvent(event: OpsIntEventImpl) {
    const parts: string[] = [];
    const info = event.info;
    if (info) {
      this.addOpsExceptionParts(info.opsException, parts);
      const transactionError = this.getErrorMessages(info.transactionData);
      if (transactionError.length > 0) {
        parts.push(`Transaction Error: ${transactionError}`);
      }
    }

    const opsError = this.getErrorMessages(event.opsErrors);
    if (opsError.length > 0) {
      parts.push(`Ops Error: ${opsError}`);
    }
    const partsText = parts.join(", ");
    this.log(`OpsInterventionEvent - ${partsText}`);
  }

  private logSavedOrderEvent(event: SavedOrderEventImpl) {
    const parts: string[] = [];

    parts.push(`User[${event.userCode}]`);
    this.addErrorParts(event.submissionErrors, parts);

    if (event.orderDataRequest) {
      parts.push("LIMIT");
    }
    if (event.fixingOrderRequest) {
      parts.push("BMARK");
    }
    const partsText = parts.join(", ");

    this.log(`SavedOrderEvent - ${partsText}`);
  }
  private logSavedOrdersEvent(event: SavedOrderEventListImpl) {
    const parts: string[] = [];

    let limitPart = "";
    let bmPart = "";
    parts.push(`User[${event.userCode}]`);
    if (event.orderDataRequests?.length) {
      limitPart = `Limit count:${event.orderDataRequests.length}`;
      parts.push(limitPart);
    }
    if (event.fixingOrderRequests?.length) {
      bmPart = `BM count:${event.fixingOrderRequests.length}`;
      parts.push(bmPart);
    }

    const partsText = parts.join(", ");
    this.log(`SavedOrderEventList - ${partsText}`);
  }

  private logDealEvent(deal: DealImpl) {
    const parts: string[] = [];

    // Lots more potentially can be logged here but this'll do for a start
    parts.push(`for Order: ${deal.orderId.toString()}(${deal.dealRef})`);

    // For security, don't log amount for the moment
    // if (deal.amt1) {
    //   parts.push(`${deal.isBuy ? "Buy" : "Sell"} Dealt Amount: ${deal.amt1.toNumber()}`);
    // }
    parts.push(`Deal state:${OrderDealState[deal.dealStateId]}`);

    const partsText = parts.join(", ");
    this.log(`DealImpl - ${partsText}`);
  }

  private logSystemMonitorEvent(event: SystemMonitorEventImpl) {
    if (event.dataMap) {
      const parts: string[] = [];

      const node = event.dataMap.get(SYSMON_EVENT_SOURCE);
      const fqdn = event.dataMap.get(SYSMON_EVENT_FQDN);
      const status = event.dataMap.get(SYSMON_EVENT_STATUS_DISP_TXT);
      const connectionGroup = event.dataMap.get(SYSMON_EVENT_GROUP_FQDN_DISP_TXT + ".1");

      parts.push(`Node ${node}`);
      parts.push(`Key:${fqdn}`);
      parts.push(`Status:${status}`);
      parts.push(`Group:${connectionGroup}`);

      const partsText = parts.join(", ");
      this.log(`SystemMonitorEvent - ${partsText}`);
    }
  }

  private logSysCtrlEvent(event: SysCtrlEventImpl) {
    const parts: string[] = [];
    const control = event.systemControl;
    if (control) {
      parts.push(
        `Control ${SystemControlType[control.systemControlType]} (${
          SystemControlSubType[control.systemControlSubType]
        })`,
      );
      if (control.currencyPairCd) {
        parts.push(`for pair ${control.currencyPairCd}`);
      }
      parts.push(`state: ${SystemControlStatus[control.systemControlStatus]}`);
    }
    parts.push(`by user ${event.userCd}`);

    const partsText = parts.join(", ");
    this.log(`SysCtrlEvent - ${partsText}`);
  }

  private logTradingCalendarSnapShotEvent(event: TradingCalendarSnapShotImpl) {
    const parts: string[] = [];

    const runs = event.calendarRun;
    if (runs?.length > 0) {
      for (const run of runs) {
        const pair = run.baseCcyCd + run.nonBaseCcyCd;
        const errors = this.getErrorMessages(run.errors);
        const errorStr = errors.length > 0 ? "Errors: " + errors.join("\n") : "";
        const numValueDateStr = run.valueDates?.length
          ? `ValueDates(${run.valueDates?.length})`
          : "";
        const numnNdfValueDateStr = run.ndfValueDates?.length
          ? `NdfDates(${run.ndfValueDates?.length})`
          : "";
        parts.push(`[${pair} - ${numValueDateStr}${numnNdfValueDateStr} ${errorStr} ]`);
      }
    }

    const partsText = parts.join("\n");
    this.log(`TradingCalendarSnapShot - ${partsText}`);
  }

  private logTrailingStopEvent(event: TrailingStopEventImpl) {
    // const parts: string[] = [];

    // const partsText = parts.join(", ");
    this.log(`TrailingStopEvent - Order ${event.orderId}`);
  }

  private logUnsolicitedCancelLifeCycleEvent(event: UnsolicitedCancelLifeCycleEventImpl) {
    const parts: string[] = [];

    const eventType = FxLifeCycleEventType[event.eventTypeId];
    const orderType = event.benchmarkFg ? "BMARK" : "LIMIT";

    if (event.orders) {
      for (const order of event.orders) {
        parts.push(`Order ${order.orderId.toString()}`);
      }
    }
    const partsText = parts.join(", ");
    this.log(
      `UnsolicitedCancelLifeCycleEvent (${eventType}-${orderType}) - from ${event.makerName} [${partsText}]`,
    );
  }

  private logAdminModelUpdateEvent(event: AdminModelUpdateImpl) {
    const eventType = ModelUpdateType[event.eventType];
    let entityType = event.entityType;
    if (entityType) {
      entityType = this.getLastClassPart(entityType);
    }
    this.log(`AdminModelUpdateEvent (${eventType}) - for ${entityType}`);
  }

  private logAdminImportStatusEvent(event: AdminImportStatusEventImpl) {
    const entityType = AdminImportType[event.entityType];
    this.log(`AdminImportStatusEventImpl (${entityType})`);
  }

  private logSystemMessageEvent(event: SystemMessageImpl) {
    // const parts: string[] = [];

    // const partsText = parts.join(", ");
    this.log(`SystemMessageEvent - ${event.messageType} ${event.message}`);
  }

  private logApplicationMessageEvent(event: ApplicationMessageEventImpl) {
    // const parts: string[] = [];

    // const partsText = parts.join(", ");
    let message: string;
    if (
      event != null &&
      event.messageType === "InflightCheckEvent" &&
      event.message instanceof InflightCheckEventImpl
    ) {
      message = `Order ${event.message.orderData?.orderId?.toString() ?? "<UNKNOWN ORDER>"}, Rule ${
        event.message.ruleName
      }(${event.message.ruleId})`;
    } else {
      message = `${event.message}`;
    }
    this.log(`ApplicationMessageEvent - ${event.messageType} ${message}`);
  }

  private logMarketDataEvent(event: MarketDataEventImpl) {
    // No rate logging as this will cause our log file to explode - however can look at aggregating incoming rates
    // over time
    // this.log(`MarketDataEventImpl - ${event.rateData?.length??0} rates from ${event.rateSource}`);
  }

  private logManualNettingEvent(event: ManualNettingEventImpl) {
    const orderIds = event.orderIds;
    let idsPart: string;
    idsPart = orderIds?.length > 0 ? orderIds.join(", ") : "";
    if (event.hasPortfolioId) {
      this.log(
        `ManualNettingEvent - Setting portfolioId '${event.portfolioId}' on orders [${idsPart}]`,
      );
    } else {
      this.log(`ManualNettingEvent - Clearing portfolioId on orders [${idsPart}]`);
    }
  }

  private logManualNettingBulkEvent(event: ManualNettingBulkEventImpl) {
    this.log(
      `ManualNettingBulkEvent - Updating individual portfolioId on ${event.nettingPairs?.length} orders`,
    );
  }

  //********************************************************************** */
  // Helper methods

  private addRejectTypePart(rejectTypeId: RejectSubType, parts: string[]) {
    if (typeof rejectTypeId === "number" && rejectTypeId !== RejectSubType.NONE) {
      const rejectType = RejectSubType[rejectTypeId];
      parts.push(`Reject: ${rejectType}`);
    }
  }

  private addOrderDataPart(orderData: OrderData, parts: string[]): boolean {
    if (orderData) {
      const orderId = orderData.orderId.toString();
      const orderState = OrderState[orderData.orderState];
      parts.push(`Order ${orderId}(${orderState})`);
      return true;
    }
    return false;
  }

  private addOpsExceptionParts(exception: OpsExcRecord, parts: string[]) {
    if (exception) {
      const exceptionType = `${OpsExceptionType[exception.exceptionTypeId]}`;
      const id = exception.dealId
        ? ` Deal(${exception.dealId.toString()})`
        : exception.orderId
          ? ` Order ${exception.orderId.toString()}`
          : "";
      parts.push(`${exceptionType}${id}`);
      if (exception.descriptionTxt) {
        const description = this.getErrorParts(exception.descriptionTxt);
        // Should this be redacted for security?
        parts.push(`description: ${description}`);
        // parts.push(`description: <DESC>`);
      }
    }
  }
}
